import React, { useMemo, useState } from 'react';
import { X, AlertTriangle, CheckCircle2, IndianRupee, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from '../hooks/use-toast';
import { api } from '../services/api';

/**
 * 3-step bulk cancel modal:
 *   Step 1: Confirm — "Cancel X bookings?" with list
 *   Step 2: Refund prompt — only if any booking has an online payment
 *   Step 3: Result toast (handled in parent via onComplete)
 */
const BulkCancelModal = ({ open, onClose, bookings, onComplete }) => {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const onlineBookings = useMemo(
    () => bookings.filter((b) => b.razorpay_payment_id && Number(b.amount_paid || 0) > 0),
    [bookings]
  );
  const totalOnlinePaid = useMemo(
    () => onlineBookings.reduce((s, b) => s + Number(b.amount_paid || 0), 0),
    [onlineBookings]
  );
  const hasOnline = onlineBookings.length > 0;

  if (!open) return null;

  const reset = () => {
    setStep(1); setSubmitting(false); setError('');
  };
  const close = () => { reset(); onClose?.(); };

  const submit = async (refund) => {
    setError('');
    setSubmitting(true);
    try {
      const res = await api.request('/bookings/bulk-cancel', {
        method: 'POST',
        body: JSON.stringify({
          booking_ids: bookings.map((b) => b.id),
          refund: !!refund,
        }),
      });
      const refundedMsg = res.total_refunded > 0
        ? ` Refunded ₹${res.total_refunded.toFixed(2)} to ${res.refunds?.filter((r) => r.status === 'processed').length || 0} customer(s).`
        : '';
      const skippedMsg = res.skipped_count > 0
        ? ` ${res.skipped_count} skipped.`
        : '';
      const failedRefunds = (res.refunds || []).filter((r) => r.status === 'failed').length;
      const failedMsg = failedRefunds > 0
        ? ` ⚠ ${failedRefunds} refund(s) failed — please reconcile in Razorpay.`
        : '';
      toast({
        title: `${res.cancelled_count} booking(s) cancelled`,
        description: `${refundedMsg}${skippedMsg}${failedMsg}`.trim() || 'All selected bookings have been cancelled.',
        variant: failedRefunds > 0 ? 'destructive' : 'default',
      });
      onComplete?.(res);
      close();
    } catch (e) {
      setError(e?.message || 'Bulk cancel failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-2 sm:p-4"
      data-testid="bulk-cancel-modal"
    >
      <div className="bg-white w-full max-w-lg rounded-xl shadow-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-full bg-red-100">
              <AlertTriangle className="h-4 w-4 text-red-600" />
            </div>
            <h2 className="text-base sm:text-lg font-bold text-gray-900">
              {step === 1 ? `Cancel ${bookings.length} booking${bookings.length !== 1 ? 's' : ''}?` : 'Refund online payments?'}
            </h2>
          </div>
          <button
            onClick={close}
            className="p-1.5 rounded-full hover:bg-gray-100"
            disabled={submitting}
            data-testid="bulk-cancel-close"
            aria-label="Close"
          >
            <X className="h-4 w-4 text-gray-600" />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 sm:px-6 py-4 overflow-y-auto flex-1">
          {step === 1 && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                This will mark the following bookings as cancelled. Customers will receive a cancellation notification (if email is on file).
              </p>
              <div className="rounded-lg border max-h-64 overflow-y-auto divide-y">
                {bookings.map((b) => (
                  <div
                    key={b.id}
                    className="px-3 py-2 text-xs flex justify-between gap-2"
                    data-testid={`bulk-cancel-row-${b.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">
                        {b.customer_name || b.customerName || 'Customer'}
                      </div>
                      <div className="text-gray-500">
                        {b.date} · {b.start_time || b.startTime}–{b.end_time || b.endTime} · {b.court_id || b.section || b.slot_type}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">₹{Number(b.amount || 0).toFixed(0)}</div>
                      {b.razorpay_payment_id && (
                        <div className="text-[10px] text-blue-600 font-medium">online paid</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {error && (
                <div className="text-xs text-red-600">{error}</div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 flex gap-2">
                <IndianRupee className="h-4 w-4 text-amber-700 mt-0.5" />
                <div className="text-sm text-amber-900">
                  <div className="font-semibold">
                    {onlineBookings.length} booking{onlineBookings.length !== 1 ? 's have' : ' has'} online payments collected
                  </div>
                  <div className="text-xs mt-0.5">
                    Total paid online: <span className="font-bold">₹{totalOnlinePaid.toFixed(2)}</span>. Should these be refunded via Razorpay (100% of paid amount)?
                  </div>
                </div>
              </div>
              <div className="text-xs text-gray-500">
                Cash/offline bookings will be cancelled without refund regardless of your choice.
              </div>
              {error && (
                <div className="text-xs text-red-600">{error}</div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-6 py-3 border-t bg-gray-50 flex justify-between items-center gap-2">
          {step === 1 && (
            <>
              <Button variant="outline" onClick={close} disabled={submitting} data-testid="bulk-cancel-back">
                Keep Bookings
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700"
                onClick={() => {
                  if (hasOnline) setStep(2);
                  else submit(false);
                }}
                disabled={submitting}
                data-testid="bulk-cancel-continue"
              >
                {submitting && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                {hasOnline ? 'Continue →' : `Cancel ${bookings.length} Booking${bookings.length !== 1 ? 's' : ''}`}
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button
                variant="outline"
                onClick={() => submit(false)}
                disabled={submitting}
                data-testid="bulk-cancel-no-refund"
              >
                {submitting && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                No, Cancel Without Refund
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700"
                onClick={() => submit(true)}
                disabled={submitting}
                data-testid="bulk-cancel-with-refund"
              >
                {submitting && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                Yes, Cancel and Refund ₹{totalOnlinePaid.toFixed(2)}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkCancelModal;
